import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { ReloadIcon } from "@components/icons";
import { DataScienceContext } from "../context/DataScienceContext";
import DateFilter from "../filters/DateFilter";
import IntervalPicker from "../components/IntervalPicker";
import InfoBox from "@components/InfoBox/InfoBox";

function Filter({ openDateFilter, setOpenDateFilter, topOffset = "calc(4rem + 48px)" }) {
  const { t } = useTranslation();
  const {
    setStartDate,
    setEndDate,
    setInitialStartDate,
    setInitialEndDate,
    setLYaxis,
    setRYaxis,
    setLYaxisMean,
    setRYaxisMean,
    setActiveRain,
    activeDate,
    setActiveDate,
    setSeries,
    setShowIntervalPicker,
    setCustomInterval,
  } = React.useContext(DataScienceContext);

  const [dateType, setDateType] = React.useState(activeDate || 2);
  const userTriggeredPresetChangeRef = React.useRef(false);

  // Sync local dateType with context activeDate (for URL updates)
  React.useEffect(() => {
    if (activeDate !== undefined && activeDate !== dateType) {
      setDateType(activeDate);
    }
  }, [activeDate]);
  const { mobile } = useSelector((state) => state.mobile);
  const dateSidebarWidth = mobile ? "min(88vw, 320px)" : "220px";

  const endDate = moment().format("YYYY-MM-DD");
  const oneYear = moment().subtract(1, "year").format("YYYY-MM-DD");
  const sixMonths = moment().subtract(6, "months").format("YYYY-MM-DD");
  const threeWeeks = moment().subtract(3, "weeks").format("YYYY-MM-DD");
  const oneWeek = moment().subtract(1, "week").format("YYYY-MM-DD");
  const oneDay = moment().subtract(1, "day").format("YYYY-MM-DD");

  React.useEffect(() => {
    if (!userTriggeredPresetChangeRef.current) return;
    userTriggeredPresetChangeRef.current = false;
    switch (dateType) {
      case 0:
        setStartDate(oneYear);
        setEndDate(endDate);
        setInitialStartDate(oneYear);
        setInitialEndDate(endDate);
        setActiveDate(0);
        break;
      case 1:
        setStartDate(sixMonths);
        setEndDate(endDate);
        setInitialStartDate(sixMonths);
        setInitialEndDate(endDate);
        setActiveDate(1);
        break;

      case 2:
        setStartDate(threeWeeks);
        setEndDate(endDate);
        setInitialStartDate(threeWeeks);
        setInitialEndDate(endDate);
        setActiveDate(2);
        break;

      case 3:
        setStartDate(oneWeek);
        setEndDate(endDate);
        setInitialStartDate(oneWeek);
        setInitialEndDate(endDate);
        setActiveDate(3);
        break;
      case 4:
        setStartDate(oneDay);
        setEndDate(endDate);
        setInitialStartDate(oneDay);
        setInitialEndDate(endDate);
        setActiveDate(4);
        break;
      default:
        return;
    }
  }, [dateType]);

  const reset = () => {
    setLYaxis([]);
    setRYaxis([]);
    setLYaxisMean([]);
    setRYaxisMean([]);
    setStartDate(moment().subtract(3, "weeks").format("YYYY-MM-DD"));
    setEndDate(moment().format("YYYY-MM-DD"));
    setInitialStartDate(moment().subtract(3, "weeks").format("YYYY-MM-DD"));
    setInitialEndDate(moment().format("YYYY-MM-DD"));
    setActiveRain(false);
    setActiveDate(2); // Set to default 3-week period instead of undefined
    setDateType(2); // Also update local dateType to sync UI
    setCustomInterval(null); // Reset to auto interval
    setSeries([]);
  };

  const dates = [
    { label: t("one_year"), key: "one_year", info: t("one_year_info") },
    { label: t("six_months"), key: "six_months", info: t("six_months_info") },
    { label: t("three_weeks"), key: "three_weeks", info: t("three_weeks_info") },
    { label: t("one_week"), key: "one_week", info: t("one_week_info") },
    { label: t("one_day"), key: "one_day", info: t("one_day_info") },
  ];

  const inactiveDateStyle = {
    background: "var(--color-action-subtle, #e7f1ff)",
    color: "var(--color-action, var(--bs-primary, #0d6efd))",
    border: "1px solid var(--color-border-default, #cfe2ff)",
    fontWeight: 600,
    borderRadius: "6px",
  };

  const activeDateStyle = {
    background: "var(--color-action, var(--bs-primary, #0d6efd))",
    color: "#fff",
    border: "1px solid var(--color-action, var(--bs-primary, #0d6efd))",
    fontWeight: 600,
    borderRadius: "6px",
  };

  return (
    <div
      data-tour="datascience-date-filter"
      className="ds-date-sidebar"
      style={{
        position: "fixed",
        right: 0,
        top: topOffset,
        bottom: 0,
        width: dateSidebarWidth,
        maxWidth: "100vw",
        background: "var(--color-bg-surface, #fff)",
        borderLeft: "1px solid var(--color-border-subtle, #dee2e6)",
        zIndex: 1050,
        display: "flex",
        flexDirection: "column",
        padding: "1rem",
        transition: "transform 0.3s ease",
        transform: openDateFilter ? "translateX(0)" : "translateX(100%)",
        overflowY: "auto",
      }}
    >
      {/* Header with title and info */}
      <div
        className="ds-date-sidebar__header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem",
          paddingBottom: "0.75rem",
          borderBottom: "2px solid var(--color-action, var(--bs-primary, #0d6efd))",
        }}
      >
        <h6
          style={{
            margin: 0,
            color: "var(--color-action, var(--bs-primary, #0d6efd))",
            fontWeight: 600,
          }}
        >
          {t("time_range_selection")}
        </h6>
        <InfoBox infoTitle={t("time_range_selection")} infoContent={t("time_range_info")} />
      </div>

      {/* Interval Picker */}
      <IntervalPicker />

      {/* Interval Settings Section */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
        <button
          className="btn btn-sm d-flex align-items-center justify-content-center gap-2"
          title={t("data_interval_settings")}
          type="button"
          onClick={() => setShowIntervalPicker(true)}
          style={{
            fontSize: "0.875rem",
            flex: 1,
            background: "var(--color-action-subtle, #e7f1ff)",
            color: "var(--color-action, var(--bs-primary, #0d6efd))",
            border: "1px solid var(--color-border-default, #cfe2ff)",
            fontWeight: 600,
            borderRadius: "6px",
          }}
        >
          <span>{t("interval_settings")}</span>
        </button>
        <InfoBox infoTitle={t("interval_settings")} infoContent={t("data_interval_description")} />
      </div>

      {/* Reset Chart */}
      <div style={{ marginBottom: "1rem", display: "flex", alignItem: "center", gap: "0.5rem" }}>
        <button
          className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
          title={t("reset_info")}
          type="button"
          onClick={reset}
          style={{
            fontSize: "0.875rem",
            background: "var(--color-action-subtle, #e7f1ff)",
            color: "var(--color-action, var(--bs-primary, #0d6efd))",
            border: "1px solid var(--color-border-default, #cfe2ff)",
            fontWeight: 600,
            borderRadius: "6px",
            flex: 1,
          }}
        >
          <ReloadIcon size={16} />
          <span>{t("reset_chart")}</span>
        </button>
        <InfoBox infoTitle={t("reset_chart")} infoContent={t("reset_info")} />
      </div>
      {/* Date Range Options */}
      <div className="ds__date-filter" style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            listStyleType: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {dates.map((date, index) => (
            <div
              key={date.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <button
                className={`btn btn-sm w-100 d-flex align-items-center justify-content-center ${
                  dateType === index ? "" : ""
                }`}
                style={{
                  ...(dateType === index ? activeDateStyle : inactiveDateStyle),
                  flex: 1,
                  padding: "0.5rem",
                  cursor: "pointer",
                }}
                onClick={() => {
                  userTriggeredPresetChangeRef.current = true;
                  setDateType(index);
                }}
              >
                {date.label}
              </button>
              <InfoBox infoTitle={date.label} infoContent={date.info} />
            </div>
          ))}

          {/* Custom Date Range */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <div
              style={{
                ...(dateType === 5 ? activeDateStyle : inactiveDateStyle),
                flex: 1,
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <DateFilter setDateType={setDateType} />
            </div>
            <InfoBox infoTitle={t("custom_range")} infoContent={t("custom_range_info")} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filter;
